import { useEffect, useRef, useState, useCallback } from "react";
import { GitPullRequest } from "lucide-react";
import datadogLogo from "@/assets/datadog-logo.png";
import newrelicLogo from "@/assets/newrelic-logo.png";
import sentryLogo from "@/assets/sentry-logo-github.png";
import zipyLogo from "@/assets/zipy-logo.png";
import slackLogo from "@/assets/slack-logo.png";

interface Particle {
  id: number;
  sourceIndex: number;
  phase: "to-healion" | "to-git" | "to-pr";
  progress: number;
  gitTarget: number;
}

const SOURCES = [
  { name: "Datadog", color: "hsl(270, 65%, 55%)", logo: datadogLogo },
  { name: "New Relic", color: "hsl(160, 70%, 45%)", logo: newrelicLogo },
  { name: "Sentry", color: "hsl(340, 70%, 50%)", logo: sentryLogo },
  { name: "Zipy", color: "hsl(200, 80%, 50%)", logo: zipyLogo },
  { name: "Lovable", color: "hsl(330, 80%, 60%)", logo: null },
];

const GIT_TARGETS = [
  { name: "GitHub" },
  { name: "GitLab" },
  { name: "Bitbucket" },
];

// All positions as percentages for full responsiveness
const getSourcePos = (i: number) => ({ x: 10, y: 14 + i * 18 });
const healionPos = { x: 42, y: 50 };
const getGitPos = (i: number) => ({ x: 74, y: 25 + i * 25 });
const prPos = { x: 90, y: 38 };
const slackPos = { x: 90, y: 65 };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const getParticlePos = (p: Particle) => {
  const src = getSourcePos(p.sourceIndex);
  const git = getGitPos(p.gitTarget);
  switch (p.phase) {
    case "to-healion":
      return { x: lerp(src.x + 6, healionPos.x - 6, p.progress), y: lerp(src.y, healionPos.y, p.progress) };
    case "to-git":
      return { x: lerp(healionPos.x + 14, git.x - 4, p.progress), y: lerp(healionPos.y, git.y, p.progress) };
    case "to-pr":
      return { x: lerp(git.x + 4, prPos.x - 3, p.progress), y: lerp(git.y, prPos.y, p.progress) };
  }
};

export const BugFlowAnimation = () => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const nextId = useRef(0);
  const animRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // IntersectionObserver to only animate when visible (perf + battery)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const tickRef = useRef<() => void>();

  const tick = useCallback(() => {
    setParticles((prev) =>
      prev
        .map((p) => {
          let newProgress = p.progress + 0.012;
          let newPhase = p.phase;
          if (newProgress >= 1) {
            if (p.phase === "to-healion") { newPhase = "to-git"; newProgress = 0; }
            else if (p.phase === "to-git") { newPhase = "to-pr"; newProgress = 0; }
            else return null as unknown as Particle;
          }
          return { ...p, progress: newProgress, phase: newPhase };
        })
        .filter(Boolean)
    );
    animRef.current = requestAnimationFrame(() => tickRef.current?.());
  }, []);

  tickRef.current = tick;

  useEffect(() => {
    if (!isVisible) return;

    const isMobile = window.innerWidth < 640;
    const maxParticles = isMobile ? 8 : 18;
    const spawnRate = isMobile ? 1400 : 900;

    const spawnInterval = setInterval(() => {
      setParticles((prev) => {
        if (prev.length > maxParticles) return prev;
        return [...prev, {
          id: nextId.current++,
          sourceIndex: Math.floor(Math.random() * SOURCES.length),
          phase: "to-healion" as const,
          progress: 0,
          gitTarget: Math.floor(Math.random() * GIT_TARGETS.length),
        }];
      });
    }, spawnRate);

    animRef.current = requestAnimationFrame(() => tickRef.current?.());

    return () => {
      clearInterval(spawnInterval);
      cancelAnimationFrame(animRef.current);
    };
  }, [isVisible]);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-5xl mx-auto bg-card border border-border rounded-xl overflow-hidden"
      style={{ aspectRatio: "16 / 9" }}
      role="img"
      aria-label="Animated diagram showing bugs flowing from monitoring tools through Healion AI to GitHub repos and producing ready PRs with Slack notifications"
    >
      {/* SVG connection lines */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/* Sources → Healion */}
        {SOURCES.map((_, i) => {
          const s = getSourcePos(i);
          return (
            <line key={`s-h-${i}`} x1={s.x + 8} y1={s.y} x2={healionPos.x - 7} y2={healionPos.y}
              stroke="hsl(var(--border))" strokeWidth="0.15" strokeDasharray="0.5,0.5" opacity={0.4} />
          );
        })}

        {/* Git → PR */}
        {GIT_TARGETS.map((_, i) => {
          const g = getGitPos(i);
          return (
            <line key={`g-p-${i}`} x1={g.x + 5} y1={g.y} x2={prPos.x - 3} y2={prPos.y}
              stroke="hsl(var(--border))" strokeWidth="0.15" strokeDasharray="0.5,0.5" opacity={0.4} />
          );
        })}

        {/* PR → Slack */}
        <line x1={prPos.x} y1={prPos.y + 4} x2={slackPos.x} y2={slackPos.y - 4}
          stroke="hsl(var(--accent))" strokeWidth="0.25" opacity={0.5} />
        <polygon
          points={`${slackPos.x - 1},${slackPos.y - 5} ${slackPos.x + 1},${slackPos.y - 5} ${slackPos.x},${slackPos.y - 3.5}`}
          fill="hsl(var(--accent))" opacity={0.6}
        />
      </svg>

      {/* Particles */}
      {particles.map((p) => {
        const pos = getParticlePos(p);
        const color = SOURCES[p.sourceIndex].color;
        const isAccent = p.phase === "to-pr";
        return (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              width: "clamp(5px, 0.8vw, 10px)",
              height: "clamp(5px, 0.8vw, 10px)",
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              backgroundColor: isAccent ? "hsl(var(--accent))" : color,
              boxShadow: `0 0 6px ${isAccent ? "hsl(var(--accent))" : color}`,
              transform: "translate3d(-50%, -50%, 0)",
              willChange: "transform",
            }}
          />
        );
      })}

      {/* Source boxes */}
      {SOURCES.map((s, i) => {
        const pos = getSourcePos(i);
        return (
          <div key={s.name} className="absolute flex items-center justify-center"
            style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}>
            <div className="px-1.5 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg border border-border bg-muted/50 flex items-center gap-1 sm:gap-2">
              {s.logo ? (
                <img src={s.logo} alt={s.name} className="w-3 h-3 sm:w-4 sm:h-4 object-contain" loading="lazy" />
              ) : (
                <span className="text-[8px] sm:text-xs">💜</span>
              )}
              <span className="text-[7px] sm:text-xs font-mono text-foreground/80 whitespace-nowrap">{s.name}</span>
            </div>
          </div>
        );
      })}

      {/* Healion AI central box */}
      <div className="absolute flex flex-col items-center justify-center"
        style={{ left: `${healionPos.x}%`, top: `${healionPos.y}%`, transform: "translate(-50%, -50%)" }}>
        <div className="px-1 py-0.5 sm:px-3 sm:py-2 lg:px-8 lg:py-5 rounded sm:rounded-lg lg:rounded-xl bg-accent/15 border sm:border-2 border-accent shadow-xl shadow-accent/25 flex flex-col items-center gap-0 sm:gap-0.5 lg:gap-1">
          <span className="text-[6px] sm:text-xs lg:text-xl font-heading font-bold text-accent">⚡ Healion AI</span>
          <span className="text-[4px] sm:text-[7px] lg:text-sm text-accent font-bold tracking-wide leading-tight">Auto Fix Production Bugs</span>
        </div>
      </div>


      {/* Git targets */}
      {GIT_TARGETS.map((g, i) => {
        const pos = getGitPos(i);
        return (
          <div key={g.name} className="absolute flex items-center"
            style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}>
            <div className="px-1.5 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg border border-border bg-muted/50 text-[7px] sm:text-xs font-mono text-foreground/80">
              {g.name}
            </div>
          </div>
        );
      })}

      {/* PR output */}
      <div className="absolute flex items-center"
        style={{ left: `${prPos.x}%`, top: `${prPos.y}%`, transform: "translate(-50%, -50%)" }}>
        <div className="px-1 py-0.5 sm:px-1.5 sm:py-1 rounded sm:rounded-md sm:rounded-lg border border-accent/40 bg-accent/10 flex items-center gap-0.5 sm:gap-1">
          <GitPullRequest className="w-2 h-2 sm:w-2.5 sm:h-2.5 sm:w-4 sm:h-4 text-accent" />
          <span className="text-[5px] sm:text-[7px] sm:text-xs font-bold text-accent">PRs Ready</span>
        </div>
      </div>

      {/* Slack box */}
      <div className="absolute flex items-center"
        style={{ left: `${slackPos.x}%`, top: `${slackPos.y}%`, transform: "translate(-50%, -50%)" }}>
        <div className="px-1 py-0.5 sm:px-1.5 sm:py-1 rounded sm:rounded-md sm:rounded-lg border border-border bg-muted/50 flex items-center gap-0.5 sm:gap-1 sm:gap-1.5">
          <img src={slackLogo} alt="Slack" className="w-2 h-2 sm:w-3 sm:h-3 sm:w-4 sm:h-4 object-contain" loading="lazy" />
          <div className="flex flex-col">
            <span className="text-[5px] sm:text-[7px] sm:text-xs font-bold text-foreground/80">Slack</span>
            <span className="text-[4px] sm:text-[5px] sm:text-[8px] text-muted-foreground whitespace-nowrap">Get Notified</span>
          </div>
        </div>
      </div>
    </div>
  );
};
